import { isDefined } from '@sgme/fp';
import type { Action } from 'state/actions';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import type { OriginalForm, OriginalFormMap } from 'state/secondaryMarket/shared/form/original/original.model';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import { addOrReplaceKey, patchKeyIfExist, removeKey } from 'state/utils/collection';
import type { SecondaryMarketPostEventTabType } from 'state/workspaces/model';

function getTradeSizeMode(cashAmount: any, quantity: any): TradeSizeInputMode | undefined {
  if (isDefined(cashAmount)) {
    return 'amount';
  }
  if (isDefined(quantity)) {
    return 'quantity';
  }
  return undefined;
}
export function originalFormReducerFactory<
  F extends Form,
  C extends OriginalForm<F>,
  ActionType extends Exclude<OrderActionType, 'CP_ORDER'>,
  AmendFetchAction extends { type: `SECONDARY_MARKET_AMEND_${ActionType}_FETCHED` },
>(
  actionType: ActionType,
  initialMap: OriginalFormMap<F, C>,
  tabType: Exclude<SecondaryMarketPostEventTabType, 'closePriceOrder'>,
  onAmendFetched: (action: AmendFetchAction) => Partial<C>,
) {
  return (state: OriginalFormMap<F, C> = initialMap, action: Action): OriginalFormMap<F, C> => {
    switch (action.type) {
      case `SECONDARY_MARKET_AMEND_${actionType}_FETCHED` as const:
        return addOrReplaceKey(state, action.tabId, {
          cashAmount: action.data.cashAmount,
          client: action.data.client,
          clientType: action.data.clientType ?? 'Other',
          comment: action.data.comment,
          custodian: action.data.custodian,
          quantity: action.data.quantity,
          salesId: action.data.salesId,
          salesMargin: action.data.salesMargin,
          settlementDate: action.data.settlementDate,
          tradeWay: action.data.tradeWay,
          settlementType: action.data.settlementType
            ? {
                settlementType: action.data.settlementType.settlementType,
                marketType: action.data.settlementType.marketType,
              }
            : undefined,
          tradeSizeInputMode: getTradeSizeMode(action.data.cashAmount, action.data.quantity),
          priceType: action.data.priceType,
          etag: action.data.etag,
          ...onAmendFetched(action as unknown as AmendFetchAction),
        } as C);
      case `SECONDARY_MARKET_${actionType}_FORM_FIELD_CHANGED` as const:
        return patchKeyIfExist(state, action.tabId, () => ({ [action.field]: undefined }) as Partial<C>);
      case `SECONDARY_MARKET_POSTEVENT_WORKSPACE_REMOVE_TAB` as const:
        if (action.tabType === tabType) {
          return removeKey(state, action.tabId);
        }
        return state;
      default:
        return state;
    }
  };
}
